import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { branch } from 'js/utils/recompose.util';
import { usePrevious } from 'js/hooks/usePrevious';
import { useDeviceSelector } from 'js/store/hooks';
import { apply, compose, gt, map, pick } from 'ramda';
import { ManagedPopup } from 'js/components/common/Popup';
import withSwipe from 'js/components/common/Slider/withSwipe';
import { useOnClose } from 'js/components/common/PopupManager';
import { useSubscribeOnLocationChange } from 'js/hooks/useSubscribeOnLocationChange';
import { useNavigateToPrevPage } from 'js/components/system/Router/components/PageRouter/hooks';

import ActionButtons from './ActionButtons';
import { GameInfo } from './components/GameInfo';
import { GAMES_INFO_POPUP_ID } from './constants';

import style from './index.scss';

export const ANIMATION_DURATION = 350;

const animatedOpen = ({ isCardView, navigationDirection, popupRef }) => {
    if (isCardView && navigationDirection !== -1) {
        const removeClass = () => {
            popupRef.current.classList.remove(style.showPopup);
            popupRef.current.removeEventListener('animationend', removeClass);
        };
        popupRef.current.addEventListener('animationend', removeClass);
        popupRef.current.classList.add(style.showPopup);
    }
};

const setSwipeDirection = ({ swipeDeltaXPrev, swipeDeltaYPrev }, { swipeDeltaY, swipeDeltaX }, swipeDirection) => {
    const horizontal = apply(gt, map(Math.abs, [swipeDeltaX, swipeDeltaY]));
    const up = swipeDeltaYPrev < swipeDeltaY;
    const left = swipeDeltaXPrev < swipeDeltaX;

    swipeDirection.current = horizontal
        ? left && 'left' || 'right'
        : up && 'up' || 'down';
};

const toggleHtmlClassNames = (code, action = 'add') =>
    document.documentElement.classList[action](`gameInfo__${code}`);

export const GamesInfoPopupBare = ({
    id,
    data,
    refs: { layerRef },
    swipeStart,
    swipeDeltaX = null,
    swipeDeltaY = null,
    swipeStarted
}) => {
    const popupRef = useRef();
    const innerPopupRef = useRef();
    const refs = useMemo(() => ({ popupRef, innerPopupRef }), []);
    const { browserName, isMobile, isTablet, isLandscape } = useDeviceSelector(pick([
        'browserName',
        'isMobile',
        'isTablet',
        'isLandscape'
    ]));
    const swipeDirection = useRef();
    const navigateToPrevPage = useNavigateToPrevPage();
    const onClose = useOnClose(GAMES_INFO_POPUP_ID);
    const t = useTranslations();
    const useHackForSafari = browserName === 'safari' && isMobile && isLandscape;
    const isCardView = isMobile || isTablet;
    const showBackButton = isCardView && data.showBackButton;
    const popupCls = [
        style.popup,
        showBackButton ? style.withBackBtn : '',
        useHackForSafari ? style.withBottomIndent : ''
    ].join(' ');
    const buttons = showBackButton ? <ActionButtons t={t} /> : null;
    const { navigationDirection, code } = data;

    const animatedClose = useCallback(({ animate = true, closeLayer = true, navigateOnClose = true } = {}) => {
        if (isCardView) {
            if (layerRef.current && animate) {
                if (closeLayer) {
                    layerRef.current.addEventListener('animationend', onClose);
                    layerRef.current.classList.add(style.hideLayer);
                } else {
                    popupRef.current.addEventListener('animationend', onClose);
                    popupRef.current.classList.add(style.hidePopup);
                }
            } else {
                setTimeout(onClose, ANIMATION_DURATION);
            }
        } else {
            onClose();
        }

        if (navigateOnClose) {
            navigateToPrevPage();
        }
    }, [isCardView, layerRef, onClose, navigateToPrevPage]);

    const onLocationChange = useCallback(() => {
        animatedClose({ animate: false, closeLayer: false, navigateOnClose: false });
    }, [animatedClose]);

    useSubscribeOnLocationChange(onLocationChange);

    useEffect(() => {
        animatedOpen({ isCardView, navigationDirection, popupRef });
    }, [isCardView, navigationDirection, popupRef]);

    useEffect(() => {
        // todo - it doesn't seem to work
        // todo - Helmet usage override all attributes each time
        // todo - perhaps need to write common approach for Helmet
        toggleHtmlClassNames(code);

        return () => {
            toggleHtmlClassNames(code, 'remove');
        };
    }, [useHackForSafari, code]);

    const swipeDeltaYPrev = usePrevious(swipeDeltaY);
    const swipeDeltaXPrev = usePrevious(swipeDeltaX);

    useEffect(() => {
        if (swipeDeltaYPrev === swipeDeltaY && swipeDeltaXPrev === swipeDeltaX) {
            return;
        }

        if (swipeDeltaY !== 0 || swipeDeltaX !== 0) {
            setSwipeDirection({ swipeDeltaXPrev, swipeDeltaYPrev }, { swipeDeltaX, swipeDeltaY }, swipeDirection);
        }

        if (!swipeStarted
            && swipeDirection.current === 'down'
            && innerPopupRef.current.scrollTop === 0) {
            animatedClose();
        }
    });

    const { hideButtons, gameLaunchType } = data;
    const classNames = useMemo(() => pick(['close', 'inner', 'header'], style), []);

    return (
        <ManagedPopup
            id={id}
            onTouchStart={swipeStart}
            buttons={buttons}
            className={popupCls}
            classNames={classNames}
            onClose={animatedClose}
            layerClassNames={style}
            refs={refs}
        >
            <GameInfo {...{ data, hideButtons, gameLaunchType }} />
        </ManagedPopup>
    );
};

export const enhance = branch(
    () => {
        const { isMobile, isTablet } = useDeviceSelector(pick(['isMobile', 'isTablet']));

        return isMobile || isTablet;
    },
    compose(
        Cmp => props => <Cmp {...props} enableAxisY={true} />,
        withSwipe
    )
);

export const GamesInfoPopup = enhance(GamesInfoPopupBare);
